import React, { createContext, useContext, useEffect, useRef, useLayoutEffect, } from 'react';
import { useFragmentStateManager } from './FragmentStateManagerProvider';
import { FragmentState } from '../interfaces';
import { ELEMENT_KEY, ERROR_MESSAGE_KEY, Metric, Trace, } from '../../../metrics';
import { ErrorBoundary, toErrorMessage } from '../../../util';
import TENANT_ID from '../../../config/TenantId';
import Debug from '../../../util/Debug';
export const FragmentContext = createContext(undefined);
export const FragmentContextProvider = ({ fragmentName, customMetadata = undefined, children, tenantId: contextTenantId, startTime = Date.now(), }) => {
    var _a;
    const fragmentStateRef = useRef(FragmentState.Undefined);
    const { notifyStateChange, tenantId: managerTenantId } = useFragmentStateManager();
    const tenantId = (_a = contextTenantId !== null && contextTenantId !== void 0 ? contextTenantId : managerTenantId) !== null && _a !== void 0 ? _a : TENANT_ID;
    const checkMetricNameLength = (metricName) => {
        if (!__DEV__)
            return;
        const fullMetricName = `${tenantId}.${metricName}`;
        // https://docs.aws.amazon.com/pinpoint/latest/apireference/apps-application-id-events.html
        const MaxMetricNameLength = 50;
        if (fullMetricName.length > MaxMetricNameLength) {
            Debug.warn(`Fragment name '${fragmentName}' is too long that the metric '${fullMetricName}'`, ` is longer than ${MaxMetricNameLength} characters, which will not be emitted.`);
        }
    };
    const emitStateMetricsFor = (fragmentState) => {
        const metricName = `${fragmentName}${FragmentState[fragmentState]}Latency`;
        const loadTrace = Trace(metricName, tenantId, { startTime });
        loadTrace.log();
        checkMetricNameLength(metricName);
    };
    const setFragmentState = (newState) => {
        const currentState = fragmentStateRef.current;
        if (currentState === undefined || currentState < newState) {
            fragmentStateRef.current = newState;
            if (newState !== FragmentState.Undefined) {
                emitStateMetricsFor(newState);
            }
        }
        notifyStateChange === null || notifyStateChange === void 0 ? void 0 : notifyStateChange(fragmentName, fragmentStateRef.current, customMetadata);
    };
    useLayoutEffect(() => {
        if (React.Children.toArray(children).filter(Boolean).length > 0) {
            setFragmentState(FragmentState.Undefined);
        }
    }, []);
    useEffect(() => {
        if (React.Children.toArray(children).filter(Boolean).length === 0) {
            Debug.log(`Fragment ${fragmentName} has no children`);
        }
        else if (notifyStateChange && fragmentStateRef.current === FragmentState.Undefined) {
            Debug.error(`Fragment ${fragmentName} must update its state through useFragmentContext.`);
        }
    }, []);
    const initRenderTrace = Trace(`${fragmentName}InitialRender`, tenantId);
    useEffect(() => {
        initRenderTrace.log();
    }, []);
    const renderTrace = Trace(`${fragmentName}Render`, tenantId);
    useEffect(() => {
        renderTrace.log();
    });
    const uncaughtErrorHandler = (error, info) => {
        setFragmentState(FragmentState.UncaughtError);
        const errorMessage = toErrorMessage(error);
        Metric('UncaughtError', tenantId, {
            attributes: {
                [ERROR_MESSAGE_KEY]: errorMessage,
                [ELEMENT_KEY]: fragmentName,
            },
        }).put();
        Debug.error(`Uncaught error on fragment ${fragmentName}:${errorMessage}. StackTrace:${info.componentStack}`);
    };
    return (React.createElement(ErrorBoundary, { onError: uncaughtErrorHandler },
        React.createElement(FragmentContext.Provider, { value: {
                fragmentStateRef,
                setFragmentState,
            } }, children)));
};
export const useFragmentContext = () => {
    const fragmentContext = useContext(FragmentContext);
    if (!fragmentContext) {
        Debug.error('No FragmentContext available! '
            + 'useFragmentContext must be used in components within FragmentContextProvider.');
    }
    return fragmentContext;
};
//# sourceMappingURL=FragmentContextProvider.js.map